import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Camera, Pencil, Trash2, Plus, Loader2, Star } from 'lucide-react';
import { toast } from 'sonner';

export default function ImageRulesPage() {
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editRule, setEditRule] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ slot_key: '', slot_label: '', is_required: true, is_certificate_default: false, sort_order: 0, helper_text: '', example_image_key: '', example_image_caption: '', category_id: '' });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/admin/image-rules'),
      api.get('/admin/catalog/categories'),
    ]).then(([r, c]) => {
      setRules(r.data);
      setCategories(c.data);
    }).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleUpdate = async () => {
    if (!editRule) return;
    setSaving(true);
    try {
      await api.put(`/admin/catalog/image-rules/${editRule.id}`, {
        slot_label: form.slot_label,
        is_required: form.is_required,
        is_certificate_default: form.is_certificate_default,
        sort_order: form.sort_order,
        helper_text: form.helper_text || null,
        example_image_key: form.example_image_key || null,
        example_image_caption: form.example_image_caption || null,
      });
      toast.success('Image rule updated');
      setEditRule(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const handleCreate = async () => {
    if (!form.slot_key || !form.slot_label || !form.category_id) return;
    setSaving(true);
    try {
      await api.post('/admin/catalog/image-rules', form);
      toast.success('Image rule created');
      setShowCreate(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (rule) => {
    if (!confirm(`Delete slot "${rule.slot_label}"?`)) return;
    try {
      await api.delete(`/admin/catalog/image-rules/${rule.id}`);
      toast.success('Deleted');
      load();
    } catch (err) { toast.error('Failed'); }
  };

  const openEdit = (r) => {
    setForm({
      slot_key: r.slot_key, slot_label: r.slot_label, is_required: r.is_required,
      is_certificate_default: !!r.is_certificate_default,
      sort_order: r.sort_order || 0, helper_text: r.helper_text || '',
      example_image_key: r.example_image_key || '', example_image_caption: r.example_image_caption || '',
      category_id: r.category_id,
    });
    setEditRule(r);
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  const grouped = {};
  rules.forEach(r => { const cat = r.category_name || 'Unknown'; if (!grouped[cat]) grouped[cat] = []; grouped[cat].push(r); });

  return (
    <div data-testid="image-rules-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Camera className="h-4 w-4 text-ink" />
          <h1 className="text-base font-semibold text-ink">Image Rules</h1>
          <Badge variant="outline" className="text-[10px]">{rules.length} slots</Badge>
        </div>
        <Button size="sm" className="gap-1" onClick={() => { setForm({ slot_key: '', slot_label: '', is_required: true, is_certificate_default: false, sort_order: 0, helper_text: '', example_image_key: '', example_image_caption: '', category_id: categories[0]?.id || '' }); setShowCreate(true); }} data-testid="create-rule-btn">
          <Plus className="h-3 w-3" /> Add Slot
        </Button>
      </div>

      <div className="space-y-4">
        {Object.entries(grouped).map(([cat, slots]) => (
          <Card key={cat} className="bg-white border-stone/50">
            <CardContent className="p-4">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">{cat}</h3>
              <div className="space-y-2">
                {slots.sort((a,b) => (a.sort_order||0) - (b.sort_order||0)).map((s) => (
                  <div key={s.id} className="flex items-center gap-3 p-2.5 rounded bg-stone/5 border border-stone/20">
                    <Camera className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-ink font-medium">{s.slot_label}</span>
                        <span className="text-[9px] text-muted-foreground font-mono">{s.slot_key}</span>
                        {s.is_required && <Badge className="text-[8px] h-3.5" style={{background:'rgba(14,107,93,0.10)',color:'#0E6B5D'}}>Req</Badge>}
                        {s.is_certificate_default && (
                          <span title="On Certificate — default source image" data-testid={`cert-star-${s.slot_key}`}>
                            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                          </span>
                        )}
                      </div>
                      {s.helper_text && <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{s.helper_text}</p>}
                      {s.example_image_key && <p className="text-[10px] text-muted-foreground mt-0.5">Has example image</p>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => openEdit(s)} data-testid={`edit-rule-${s.slot_key}`}><Pencil className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => handleDelete(s)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-sm">Add Image Slot</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Category</Label>
              <select value={form.category_id} onChange={e => setForm({...form, category_id: e.target.value})} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-xs">
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Slot Key</Label><Input value={form.slot_key} onChange={e => setForm({...form, slot_key: e.target.value})} className="mt-1 font-mono" placeholder="front_view" /></div>
            <div><Label className="text-xs">Slot Label</Label><Input value={form.slot_label} onChange={e => setForm({...form, slot_label: e.target.value})} className="mt-1" placeholder="Front View" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.is_required} onCheckedChange={v => setForm({...form, is_required: v})} /><Label className="text-xs">Required</Label></div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_certificate_default} onCheckedChange={v => setForm({...form, is_certificate_default: v})} data-testid="create-cert-default-switch" />
              <Label className="text-xs flex items-center gap-1">On Certificate <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" /></Label>
            </div>
            <div><Label className="text-xs">Helper Text</Label><Textarea value={form.helper_text} onChange={e => setForm({...form, helper_text: e.target.value})} className="mt-1" rows={2} placeholder="Instructions for the customer..." /></div>
            <div><Label className="text-xs">Sort Order</Label><Input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value)||0})} className="mt-1 w-20" /></div>
          </div>
          <DialogFooter>
            <Button size="sm" onClick={handleCreate} disabled={saving}>{saving ? 'Creating...' : 'Create Slot'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editRule} onOpenChange={() => setEditRule(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-sm">Edit Image Slot: {editRule?.slot_label}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Slot Label</Label><Input value={form.slot_label} onChange={e => setForm({...form, slot_label: e.target.value})} className="mt-1" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.is_required} onCheckedChange={v => setForm({...form, is_required: v})} /><Label className="text-xs">Required</Label></div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_certificate_default} onCheckedChange={v => setForm({...form, is_certificate_default: v})} data-testid="edit-cert-default-switch" />
              <Label className="text-xs flex items-center gap-1">On Certificate <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" /></Label>
            </div>
            <div><Label className="text-xs">Helper Text (customer-facing)</Label><Textarea value={form.helper_text} onChange={e => setForm({...form, helper_text: e.target.value})} className="mt-1" rows={2} placeholder="Instructions for the customer..." /></div>
            <div><Label className="text-xs">Example Image Storage Key</Label><Input value={form.example_image_key} onChange={e => setForm({...form, example_image_key: e.target.value})} className="mt-1 font-mono text-[10px]" placeholder="example-images/front-view.jpg" /></div>
            <div><Label className="text-xs">Example Image Caption</Label><Input value={form.example_image_caption} onChange={e => setForm({...form, example_image_caption: e.target.value})} className="mt-1" placeholder="Show the front of the item clearly" /></div>
            <div><Label className="text-xs">Sort Order</Label><Input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value)||0})} className="mt-1 w-20" /></div>
          </div>
          <DialogFooter>
            <Button size="sm" onClick={handleUpdate} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
